/**
 * Admin — Hero Slides Manager
 * Manage the landing page top carousel images.
 */
import { useEffect, useRef, useState } from 'react';
import { GripVertical, ImagePlus, Pencil, Plus, Save, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { uploadHref } from '../../utils/uploadHref';

const blankSlide = () => ({ title: '', subtitle: '', image: '', linkUrl: '', roleType: '', isActive: true, sortOrder: 100 });

const ROLE_OPTIONS = [
    { value: '', label: '— Not a top performer —' },
    { value: 'BDA', label: 'BDA — Top performer' },
    { value: 'BDE', label: 'BDE — Top performer' },
    { value: 'BDM', label: 'BDM — Top performer' },
];

export default function HeroSlides() {
    const [slides, setSlides] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/hero-slides/admin');
            setSlides(r.data.items || []);
        } catch { /* toasted by interceptor */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggleActive = async (slide) => {
        try {
            await api.patch(`/hero-slides/${slide._id}`, { isActive: !slide.isActive });
            toast.success(slide.isActive ? 'Slide hidden' : 'Slide active');
            load();
        } catch { /* toasted */ }
    };

    const remove = async (slide) => {
        if (!confirm(`Delete slide "${slide.title || 'Untitled'}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/hero-slides/${slide._id}`);
            toast.success('Slide deleted');
            load();
        } catch { /* toasted */ }
    };

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">Hero Slides</h1>
                    <p className="text-sm text-ink-500 mt-1">
                        Manage hero images. Slides tagged BDA, BDE or BDM appear in the landing hero top-performer carousel.
                    </p>
                </div>
                <button className="ag-btn-primary" onClick={() => setEditing(blankSlide())}>
                    <Plus className="h-4 w-4" /> Add slide
                </button>
            </div>

            {loading && <div className="py-16 text-center text-ink-500">Loading…</div>}

            {!loading && slides.length === 0 && (
                <div className="py-20 text-center text-ink-500">
                    <ImagePlus className="h-12 w-12 mx-auto mb-3 text-ink-300" />
                    <p className="font-medium">No slides yet</p>
                    <p className="text-sm mt-1">Click "Add slide" to upload your first carousel image.</p>
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {slides.map(slide => (
                    <div key={slide._id} className={`ag-card overflow-hidden ${!slide.isActive ? 'opacity-60' : ''}`}>
                        {/* Image */}
                        <div className="relative h-44 bg-ink-100 rounded-t-xl overflow-hidden">
                            {slide.image ? (
                                <img
                                    src={uploadHref(slide.image)}
                                    alt={slide.title || 'Slide'}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-ink-400">
                                    <ImagePlus className="h-10 w-10" />
                                </div>
                            )}
                            <div className="absolute top-2 left-2 flex gap-1">
                                {slide.roleType && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-gold/90 text-brand-dark">
                                        Top {slide.roleType}
                                    </span>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${slide.isActive ? 'bg-green-100 text-green-700' : 'bg-ink-200 text-ink-600'}`}>
                                    {slide.isActive ? 'Active' : 'Hidden'}
                                </span>
                            </div>
                        </div>

                        <div className="p-4">
                            <p className="font-semibold text-ink-900 truncate">{slide.title || <span className="text-ink-400 italic">No title</span>}</p>
                            {slide.subtitle && <p className="text-xs text-ink-600 mt-0.5 truncate">{slide.subtitle}</p>}
                            <p className="text-[10px] text-ink-400 mt-1">Order: {slide.sortOrder}</p>

                            <div className="mt-3 flex items-center gap-2">
                                <button className="ag-btn-ghost text-xs py-1 px-2 flex-1" onClick={() => setEditing({ ...slide })}>
                                    <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                                <button
                                    className="ag-btn-ghost text-xs py-1 px-2"
                                    title={slide.isActive ? 'Hide slide' : 'Show slide'}
                                    onClick={() => toggleActive(slide)}
                                >
                                    {slide.isActive
                                        ? <ToggleRight className="h-4 w-4 text-green-600" />
                                        : <ToggleLeft className="h-4 w-4 text-ink-400" />}
                                </button>
                                <button
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition"
                                    title="Delete"
                                    onClick={() => remove(slide)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <SlideEditor
                    slide={editing}
                    onClose={(saved) => { setEditing(null); if (saved) load(); }}
                />
            )}
        </div>
    );
}

function SlideEditor({ slide, onClose }) {
    const [form, setForm] = useState({ ...slide });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef();
    const isNew = !form._id;

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('photo', file);
            const r = await api.post('/hero-slides/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setForm(f => ({ ...f, image: r.data.url }));
            toast.success('Image uploaded');
        } catch { /* toasted */ }
        finally { setUploading(false); }
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.image) return toast.error('Please upload an image first');
        setBusy(true);
        try {
            if (isNew) await api.post('/hero-slides', form);
            else       await api.patch(`/hero-slides/${form._id}`, form);
            toast.success(isNew ? 'Slide created' : 'Slide updated');
            onClose(true);
        } catch { /* toasted */ }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form onSubmit={submit} className="bg-white rounded-xl2 shadow-soft w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <h3 className="font-heading font-bold text-xl">{isNew ? 'Add slide' : 'Edit slide'}</h3>
                    <button type="button" onClick={() => onClose(false)} className="text-ink-500 hover:text-ink-900 text-xl">✕</button>
                </div>

                {/* Image preview + upload */}
                <div>
                    <label className="ag-label">Slide image *</label>
                    {form.image ? (
                        <div className="relative rounded-xl overflow-hidden h-44 bg-ink-100 mb-2">
                            <img src={uploadHref(form.image)} alt="preview" className="w-full h-full object-cover" />
                            <button
                                type="button"
                                className="absolute top-2 right-2 ag-btn-ghost text-xs py-1 px-2 bg-white/80"
                                onClick={() => fileRef.current?.click()}
                            >
                                Change
                            </button>
                        </div>
                    ) : (
                        <div
                            className="flex flex-col items-center justify-center h-36 rounded-xl border-2 border-dashed border-ink-300 hover:border-brand-teal/60 cursor-pointer transition mb-2"
                            onClick={() => fileRef.current?.click()}
                        >
                            <ImagePlus className="h-8 w-8 text-ink-400 mb-1" />
                            <span className="text-xs text-ink-500">{uploading ? 'Uploading…' : 'Click to upload image'}</span>
                        </div>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
                    <p className="text-[11px] text-ink-400">Or paste an image URL:</p>
                    <input
                        className="ag-input mt-1 text-xs font-mono"
                        value={form.image}
                        onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                        placeholder="https://... or /uploads/hero-slides/..."
                    />
                </div>

                <div className="grid grid-cols-1 gap-3">
                    <div>
                        <label className="ag-label">Title</label>
                        <input className="ag-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Welcome to AGEON" />
                    </div>
                    <div>
                        <label className="ag-label">Subtitle</label>
                        <input className="ag-input" value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="Role or tagline (optional)" />
                    </div>
                    <div>
                        <label className="ag-label">Top performer role</label>
                        <select
                            className="ag-input"
                            value={form.roleType || ''}
                            onChange={e => setForm(f => ({ ...f, roleType: e.target.value }))}
                        >
                            {ROLE_OPTIONS.map(o => (
                                <option key={o.value || 'none'} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-ink-400 mt-1">BDA, BDE and BDM slides rotate in the landing hero carousel.</p>
                    </div>
                    <div>
                        <label className="ag-label">Link URL (optional)</label>
                        <input className="ag-input" value={form.linkUrl} onChange={e => setForm(f => ({ ...f, linkUrl: e.target.value }))} placeholder="https://..." />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="ag-label">Sort order</label>
                            <input type="number" className="ag-input" value={form.sortOrder} onChange={e => setForm(f => ({ ...f, sortOrder: Number(e.target.value) }))} />
                        </div>
                        <label className="flex items-end gap-2 pb-2 cursor-pointer">
                            <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                            <span className="text-sm">Active (visible)</span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button className="ag-btn-primary" disabled={busy || uploading}>
                        <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save slide'}
                    </button>
                </div>
            </form>
        </div>
    );
}
